import { Alert } from "@mui/material";
import { omit } from "lodash";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  GetOrgMaintenanceTaskDetailsQuery,
  useAddOrgMaintenanceTaskMutation,
  useEditOrgMaintenanceTaskMutation,
  useGetOrgMaintenanceTaskDetailsQuery,
} from "../../../graphql/generated";
import { showDialog } from "../../../redux/slices/alert/Alert.slice";
import LoadingOverlay from "../../common/LoadingOverlay";
import OrgMaintenanceTaskForm, {
  OrgMaintenanceTaskFormProps,
} from "./OrgMaintenanceTaskForm";

type OrgMaintenanceTaskFormContainerProps = Omit<
  OrgMaintenanceTaskFormProps,
  "onSave" | "saving"
> & {
  orgMaintenanceTaskId?: string;
  onLoad?: (
    orgMaintenanceTask: GetOrgMaintenanceTaskDetailsQuery["orgMaintenanceTaskById"]
  ) => void;
  onSaved?: (
    orgMaintenanceTask: GetOrgMaintenanceTaskDetailsQuery["orgMaintenanceTaskById"]
  ) => void;
};

function OrgMaintenanceTaskFormContainer({
  orgMaintenanceTaskId,
  onLoad,
  onSaved,
  ...props
}: OrgMaintenanceTaskFormContainerProps) {
  const addOrgMaintenanceTaskMutation = useAddOrgMaintenanceTaskMutation();
  const editOrgMaintenanceTaskMutation = useEditOrgMaintenanceTaskMutation();
  const initialOrgMaintenanceTaskId = useRef(orgMaintenanceTaskId);

  // We call this hook conditionally because the component
  // should never rerender with a different value for
  // businessEntityId, but if it does we don't update
  // the ref anyway so this is safe
  const getOrgMaintenanceTaskQuery = initialOrgMaintenanceTaskId.current
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useGetOrgMaintenanceTaskDetailsQuery(
        {
          id: initialOrgMaintenanceTaskId.current,
        },
        {
          refetchOnWindowFocus: false,
          retry: false,
        }
      )
    : null;
  // const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (getOrgMaintenanceTaskQuery?.data?.orgMaintenanceTaskById) {
      onLoad?.(getOrgMaintenanceTaskQuery.data?.orgMaintenanceTaskById);
    }
  }, [onLoad, getOrgMaintenanceTaskQuery?.data]);

  if (orgMaintenanceTaskId) {
    if (
      getOrgMaintenanceTaskQuery?.isLoading ||
      getOrgMaintenanceTaskQuery?.isFetching
    ) {
      return <LoadingOverlay loading />;
    }
    if (getOrgMaintenanceTaskQuery?.error) {
      return (
        <Alert severity="error">
          An error occurred while fetching the maintenanceTask.{" "}
          {(getOrgMaintenanceTaskQuery?.error as Error).message ||
            "Unknown error"}
        </Alert>
      );
    }
  }

  return (
    <OrgMaintenanceTaskForm
      initialMaintenanceTask={
        getOrgMaintenanceTaskQuery?.data?.orgMaintenanceTaskById
      }
      saving={
        addOrgMaintenanceTaskMutation.isLoading ||
        editOrgMaintenanceTaskMutation.isLoading
      }
      onSave={async (orgMaintenanceTask) => {
        try {
          if (orgMaintenanceTaskId) {
            const result = await editOrgMaintenanceTaskMutation.mutateAsync({
              id: orgMaintenanceTaskId,
              editOrgMaintenanceTaskData: omit(orgMaintenanceTask, [
                "_id",
                "assignedToUser",
              ]),
            });
            onSaved?.({
              ...orgMaintenanceTask,
              _id: result.editOrgMaintenanceTask._id,
            });
            // navigate(`/maintenanceTasks/details/${result.editMaintenanceTask._id}`);
          } else {
            const result = await addOrgMaintenanceTaskMutation.mutateAsync({
              newOrgMaintenanceTaskData: {
                ...orgMaintenanceTask,
              },
            });
            onSaved?.({
              ...orgMaintenanceTask,
              _id: result.addOrgMaintenanceTask._id,
            });
            // navigate(`/maintenanceTasks/details/${result.addMaintenanceTask._id}`);
            // window.analytics?.track("MaintenanceTask Created", {
            //   maintenanceTaskId: result.addMaintenanceTask._id,
            // });
            // window.analytics?.identify({
            //   maintenanceTaskCreated: true,
            //   lastMaintenanceTaskCreationDate: new Date(),
            //   lastMaintenanceTaskCreationDateOnly: new Date()
            //     .toISOString()
            //     .split("T")[0],
            //   numberOfMaintenanceTasksCreated:
            //     (window.analytics?.user?.()?.traits?.()?.numberOfMaintenanceTasksCreated ||
            //       0) + 1,
            // });
            // window.analytics?.group(window.analytics?.group?.()?.id?.(), {
            //   maintenanceTaskCreated: true,
            //   lastMaintenanceTaskCreationDate: new Date(),
            //   lastMaintenanceTaskCreationDateOnly: new Date()
            //     .toISOString()
            //     .split("T")[0],
            //   numberOfMaintenanceTasksCreated:
            //     (window.analytics?.group?.()?.traits?.()?.numberOfMaintenanceTasksCreated ||
            //       0) + 1,
            // });
          }
        } catch (error) {
          dispatch(
            showDialog({
              title: "Error",
              description:
                `An error occurred while ${
                  orgMaintenanceTaskId ? "editing" : "creating"
                } the maintenanceTask. ` + (error as Error).message,
              type: "error",
            })
          );
        }
      }}
      {...props}
    />
  );
}

export default OrgMaintenanceTaskFormContainer;
